import { Controller } from "@hotwired/stimulus"

// Thumbs up / down recommendation input backed by a hidden field. Picking a thumb
// reveals a review textarea. Leaving the vote unset means "no recommendation"
// (nothing is saved), and zeroed wheels mean "no duration". The two Clear buttons are
// scoped to their own section: neither one touches the other's fields.
export default class extends Controller {
  static targets = [
    "input",
    "up",
    "down",
    "reviewField",
    "prompt",
    "body",
    "counter",
    "hours",
    "minutes",
    "hoursWheel",
    "minutesWheel",
    "hoursOption",
    "minutesOption",
    "recommendClear",
    "durationClear"
  ]
  static values = {
    recommended: { type: String, default: "" },
    maxLength: { type: Number, default: 1000 },
    upPrompt: String,
    downPrompt: String
  }

  static UP_CLASSES = ["border-green-500", "bg-green-50", "text-(--thing-tile-selected-icon)"]
  static DOWN_CLASSES = ["border-red-500", "bg-red-50", "text-red-700"]
  static SCROLL_SETTLE_MS = 80

  connect() {
    this.render()
    this.countChars()
    this.positionWheels()
    this.renderDurationClear()
  }

  disconnect() {
    clearTimeout(this.hoursScrollTimer)
    clearTimeout(this.minutesScrollTimer)
  }

  selectUp() {
    this.recommendedValue = "true"
    this.render()
  }

  selectDown() {
    this.recommendedValue = "false"
    this.render()
  }

  clear() {
    this.recommendedValue = ""
    if (this.hasBodyTarget) this.bodyTarget.value = ""
    this.render()
    this.countChars()
  }

  clearDuration() {
    this.selectValue("hours", 0)
    this.selectValue("minutes", 0)
  }

  countChars() {
    if (!this.hasCounterTarget || !this.hasBodyTarget) return

    const length = this.bodyTarget.value.length
    this.counterTarget.textContent = `${length}/${this.maxLengthValue}`
  }

  onHoursWheelScroll() {
    clearTimeout(this.hoursScrollTimer)
    this.hoursScrollTimer = setTimeout(() => this.settleWheel("hours"), this.constructor.SCROLL_SETTLE_MS)
  }

  onMinutesWheelScroll() {
    clearTimeout(this.minutesScrollTimer)
    this.minutesScrollTimer = setTimeout(() => this.settleWheel("minutes"), this.constructor.SCROLL_SETTLE_MS)
  }

  onHoursWheelKeydown(event) {
    this.handleWheelKeydown(event, "hours")
  }

  onMinutesWheelKeydown(event) {
    this.handleWheelKeydown(event, "minutes")
  }

  handleWheelKeydown(event, kind) {
    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.stepWheel(kind, 1)
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.stepWheel(kind, -1)
    }
  }

  stepHoursUp() {
    this.stepWheel("hours", 1)
  }

  stepHoursDown() {
    this.stepWheel("hours", -1)
  }

  stepMinutesUp() {
    this.stepWheel("minutes", 1)
  }

  stepMinutesDown() {
    this.stepWheel("minutes", -1)
  }

  stepWheel(kind, direction) {
    const options = this.optionTargets(kind)
    const valueField = this.valueTarget(kind)
    if (options.length === 0 || !valueField) return

    const currentValue = String(valueField.value)
    const currentIndex = options.findIndex((option) => option.dataset.value === currentValue)
    const nextIndex = Math.min(Math.max((currentIndex < 0 ? 0 : currentIndex) + direction, 0), options.length - 1)

    this.selectValue(kind, options[nextIndex].dataset.value)
  }

  settleWheel(kind) {
    const wheel = this.wheelTarget(kind)
    const options = this.optionTargets(kind)
    if (!wheel || options.length === 0) return

    const itemHeight = options[0].offsetHeight
    if (!itemHeight) return

    const index = Math.round(wheel.scrollTop / itemHeight)
    const clamped = Math.min(Math.max(index, 0), options.length - 1)
    const target = clamped * itemHeight

    if (Math.abs(wheel.scrollTop - target) >= 1) wheel.scrollTop = target

    this.selectValue(kind, options[clamped].dataset.value)
  }

  selectValue(kind, value) {
    const valueField = this.valueTarget(kind)
    if (valueField) valueField.value = String(value)

    this.scrollWheelToValue(kind, value)
    this.markSelectedOption(kind, value)
    this.renderDurationClear()
  }

  renderDurationClear() {
    if (!this.hasDurationClearTarget) return

    const hours = this.hasHoursTarget ? Number(this.hoursTarget.value) : 0
    const minutes = this.hasMinutesTarget ? Number(this.minutesTarget.value) : 0

    this.durationClearTarget.classList.toggle("hidden", hours === 0 && minutes === 0)
  }

  positionWheels() {
    if (this.hasHoursTarget) this.scrollWheelToValue("hours", this.hoursTarget.value)
    if (this.hasMinutesTarget) this.scrollWheelToValue("minutes", this.minutesTarget.value)
  }

  scrollWheelToValue(kind, value) {
    const wheel = this.wheelTarget(kind)
    const options = this.optionTargets(kind)
    if (!wheel || options.length === 0) return

    const index = options.findIndex((option) => option.dataset.value === String(value))
    if (index < 0) return

    const itemHeight = options[0].offsetHeight
    if (!itemHeight) return

    wheel.scrollTop = index * itemHeight
  }

  markSelectedOption(kind, value) {
    this.optionTargets(kind).forEach((option) => {
      option.setAttribute("aria-selected", option.dataset.value === String(value) ? "true" : "false")
    })
  }

  render() {
    const choice = this.recommendedValue
    this.inputTarget.value = choice

    this.paintButton(this.upTarget, this.constructor.UP_CLASSES, choice === "true")
    this.paintButton(this.downTarget, this.constructor.DOWN_CLASSES, choice === "false")

    if (this.hasRecommendClearTarget) {
      this.recommendClearTarget.classList.toggle("hidden", choice === "")
    }

    if (!this.hasReviewFieldTarget) return

    this.reviewFieldTarget.classList.toggle("hidden", choice === "")
    if (this.hasPromptTarget) {
      if (choice === "true") this.promptTarget.textContent = this.upPromptValue
      if (choice === "false") this.promptTarget.textContent = this.downPromptValue
    }
  }

  paintButton(button, activeClasses, active) {
    activeClasses.forEach((klass) => button.classList.toggle(klass, active))
    button.classList.toggle("text-gray-600", !active)
    button.setAttribute("aria-pressed", active ? "true" : "false")
  }

  wheelTarget(kind) {
    if (kind === "hours") return this.hasHoursWheelTarget ? this.hoursWheelTarget : null
    return this.hasMinutesWheelTarget ? this.minutesWheelTarget : null
  }

  optionTargets(kind) {
    if (kind === "hours") return this.hasHoursOptionTarget ? this.hoursOptionTargets : []
    return this.hasMinutesOptionTarget ? this.minutesOptionTargets : []
  }

  valueTarget(kind) {
    if (kind === "hours") return this.hasHoursTarget ? this.hoursTarget : null
    return this.hasMinutesTarget ? this.minutesTarget : null
  }
}
